import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Heart, ShoppingCart, Eye, Palette, Loader2, ArrowUpDown,
  DollarSign, Users, Sparkles, Upload, X, Check
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_LABELS = {
  "t-shirt": "T-Shirt", "hoodie": "Hoodie", "phone-case": "Phone Case",
  "sticker": "Sticker", "mug": "Mug", "poster": "Poster",
  "snapback": "Snapback", "beanie": "Beanie", "tank-top": "Tank Top",
  "joggers": "Joggers", "tote-bag": "Tote Bag", "laptop-sleeve": "Laptop Sleeve",
  "backpack": "Backpack", "socks": "Socks", "desk-pad": "Desk Pad",
  "blanket": "Blanket", "flag": "Flag",
};

export default function GalleryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("newest");
  const [productFilter, setProductFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Buy dialog
  const [buyItem, setBuyItem] = useState(null);
  const [buyProductType, setBuyProductType] = useState("t-shirt");
  const [buyName, setBuyName] = useState("");
  const [buying, setBuying] = useState(false);

  // Publish dialog
  const [showPublish, setShowPublish] = useState(false);
  const [myDesigns, setMyDesigns] = useState([]);
  const [publishDesign, setPublishDesign] = useState(null);
  const [publishTitle, setPublishTitle] = useState("");
  const [publishDesc, setPublishDesc] = useState("");
  const [publishing, setPublishing] = useState(false);

  const fetchGallery = async () => {
    setLoading(true);
    try {
      const params = { sort_by: sortBy, page, limit: 24 };
      if (productFilter !== "all") params.product_type = productFilter;
      const res = await axios.get(`${API}/gallery`, { params });
      setItems(res.data.items || []);
      setTotalPages(res.data.pages || 1);
    } catch (err) {
      console.error("Gallery error:", err);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchGallery(); }, [sortBy, productFilter, page]);

  const handleLike = async (galleryId) => {
    if (!user) { toast.error("Sign in to like designs"); return; }
    try {
      const res = await axios.post(`${API}/gallery/${galleryId}/like`, {}, { headers });
      setItems(prev => prev.map(i =>
        i.gallery_id === galleryId ? { ...i, likes: res.data.likes, user_liked: res.data.liked } : i
      ));
    } catch { toast.error("Failed to like"); }
  };

  const handleBuy = async () => {
    if (!user) { toast.error("Sign in to purchase"); return; }
    if (!buyItem) return;
    setBuying(true);
    try {
      const res = await axios.post(`${API}/gallery/${buyItem.gallery_id}/buy`, {
        product_type: buyProductType,
        name: buyName || buyItem.title,
      }, { headers });
      if (res.data.success) {
        toast.success(`Created! Royalty of $${res.data.royalty_to_creator} goes to the designer.`);
        setBuyItem(null);
        navigate(`/merch/${res.data.merch_id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Purchase failed");
    } finally { setBuying(false); }
  };

  const loadMyDesigns = async () => {
    try {
      const res = await axios.get(`${API}/merch-studio/my-designs`, { headers });
      setMyDesigns(res.data || []);
    } catch { setMyDesigns([]); }
  };

  const handlePublish = async () => {
    if (!publishDesign || !publishTitle.trim()) {
      toast.error("Select a design and add a title");
      return;
    }
    setPublishing(true);
    try {
      const res = await axios.post(`${API}/gallery/publish`, {
        design_id: publishDesign.design_id,
        title: publishTitle,
        description: publishDesc,
      }, { headers });
      if (res.data.success) {
        toast.success("Design published to gallery!");
        setShowPublish(false);
        setPublishDesign(null);
        setPublishTitle("");
        setPublishDesc("");
        fetchGallery();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Publish failed");
    } finally { setPublishing(false); }
  };

  return (
    <div className="min-h-screen py-10 px-4 sm:px-6" data-testid="gallery-page">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-8">
          <div>
            <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/50 mb-4">
              Community Gallery
            </Badge>
            <h1 className="font-heading text-4xl sm:text-5xl uppercase tracking-tight mb-2">
              Design <span className="text-neon-cyan">Gallery</span>
            </h1>
            <p className="text-zinc-400 text-sm max-w-xl">
              Browse community designs, like your favorites, and buy them on any product. Designers earn 10% royalty on every sale.
            </p>
          </div>
          {user && (
            <Dialog open={showPublish} onOpenChange={(v) => { setShowPublish(v); if (v) loadMyDesigns(); }}>
              <DialogTrigger asChild>
                <Button className="bg-neon-magenta text-black font-bold uppercase mt-4 sm:mt-0" data-testid="publish-design-btn">
                  <Upload className="w-4 h-4 mr-2" /> Publish Your Design
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-900 border-white/10 max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-heading uppercase">Publish to Gallery</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-xs text-zinc-400">
                    Share your design with the community. You'll earn <span className="text-neon-cyan font-bold">10% royalty</span> every time someone buys a product with your design!
                  </p>

                  {/* Select Design */}
                  <div>
                    <label className="text-xs text-zinc-500 uppercase tracking-wide block mb-2">Select a Design</label>
                    {myDesigns.length === 0 ? (
                      <div className="text-center py-6 border border-dashed border-white/10 rounded-lg">
                        <p className="text-zinc-500 text-sm">No designs yet.</p>
                        <Button variant="link" onClick={() => { setShowPublish(false); navigate("/merch-studio"); }} className="text-neon-cyan text-xs">
                          Create one in the Design Studio
                        </Button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                        {myDesigns.map(d => (
                          <button
                            key={d.design_id}
                            onClick={() => setPublishDesign(d)}
                            className={`relative rounded-lg overflow-hidden border-2 transition ${
                              publishDesign?.design_id === d.design_id
                                ? "border-neon-cyan" : "border-transparent hover:border-white/20"
                            }`}
                          >
                            {d.image_base64 && (
                              <img src={`data:image/png;base64,${d.image_base64}`} alt="" className="w-full aspect-square object-cover" />
                            )}
                            {publishDesign?.design_id === d.design_id && (
                              <div className="absolute top-1 right-1 w-5 h-5 bg-neon-cyan rounded-full flex items-center justify-center">
                                <Check className="w-3 h-3 text-black" />
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Title & Description */}
                  <div>
                    <label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">Title</label>
                    <Input
                      value={publishTitle}
                      onChange={e => setPublishTitle(e.target.value)}
                      placeholder="e.g., Neon Samurai Warrior"
                      className="bg-zinc-800 border-white/10"
                      data-testid="publish-title-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">Description (optional)</label>
                    <Input
                      value={publishDesc}
                      onChange={e => setPublishDesc(e.target.value)}
                      placeholder="Tell people about your design..."
                      className="bg-zinc-800 border-white/10"
                    />
                  </div>

                  <Button
                    onClick={handlePublish}
                    disabled={publishing || !publishDesign || !publishTitle.trim()}
                    className="w-full bg-neon-magenta text-black font-bold uppercase"
                    data-testid="confirm-publish-btn"
                  >
                    {publishing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                    Publish Design
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Royalty Info Banner */}
        <div className="glass border border-purple-500/20 rounded-xl p-4 mb-8 flex items-center gap-4">
          <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center flex-shrink-0">
            <DollarSign className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <p className="text-sm font-medium">Earn 10% Royalty on Every Sale</p>
            <p className="text-xs text-zinc-400">Publish your custom designs and earn money whenever someone buys a product with your artwork.</p>
          </div>
          {!user && (
            <Button variant="outline" size="sm" className="ml-auto border-purple-500/30 text-purple-400" onClick={() => navigate("/login")}>
              Sign In
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <Select value={productFilter} onValueChange={v => { setProductFilter(v); setPage(1); }}>
            <SelectTrigger className="w-full sm:w-48 bg-black/50 border-white/10 h-10" data-testid="gallery-product-filter">
              <SelectValue placeholder="All Products" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10">
              <SelectItem value="all">All Product Types</SelectItem>
              {Object.entries(PRODUCT_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={v => { setSortBy(v); setPage(1); }}>
            <SelectTrigger className="w-full sm:w-44 bg-black/50 border-white/10 h-10" data-testid="gallery-sort">
              <ArrowUpDown className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10">
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="popular">Most Liked</SelectItem>
              <SelectItem value="purchased">Most Purchased</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Gallery Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="aspect-square bg-zinc-900/50 animate-pulse rounded-xl" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20">
            <Palette className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400 mb-2">No designs in the gallery yet</p>
            <p className="text-zinc-500 text-sm mb-6">Be the first to publish!</p>
            <Button onClick={() => navigate("/merch-studio")} className="bg-neon-magenta text-black font-bold uppercase">
              Create a Design
            </Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {items.map((item, idx) => (
                <motion.div
                  key={item.gallery_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(idx * 0.03, 0.3) }}
                  className="group rounded-xl overflow-hidden border border-white/5 bg-zinc-900/50 hover:border-white/20 transition-all"
                  data-testid={`gallery-card-${item.gallery_id}`}
                >
                  {/* Image */}
                  <div className="relative aspect-square bg-zinc-800">
                    {item.design_image_base64 ? (
                      <img
                        src={`data:image/png;base64,${item.design_image_base64}`}
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Palette className="w-12 h-12 text-zinc-600" />
                      </div>
                    )}

                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                      <Button
                        size="sm"
                        className="bg-neon-magenta text-black font-bold text-xs"
                        onClick={() => { setBuyItem(item); setBuyName(item.title); }}
                        data-testid={`buy-design-${item.gallery_id}`}
                      >
                        <ShoppingCart className="w-3 h-3 mr-1" /> Buy
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-white/30 text-xs"
                        onClick={() => handleLike(item.gallery_id)}
                        data-testid={`like-design-${item.gallery_id}`}
                      >
                        <Heart className={`w-3 h-3 mr-1 ${item.user_liked ? 'fill-red-500 text-red-500' : ''}`} />
                        {item.likes}
                      </Button>
                    </div>

                    {/* Product Type Badge */}
                    <Badge className="absolute top-2 left-2 bg-black/60 text-white border-0 text-[10px]">
                      {PRODUCT_LABELS[item.product_type] || item.product_type}
                    </Badge>
                  </div>

                  {/* Info */}
                  <div className="p-3">
                    <p className="font-medium text-sm truncate">{item.title}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[10px] text-zinc-500">by {item.creator_name}</span>
                      <div className="flex items-center gap-2 text-[10px] text-zinc-500">
                        <span className="flex items-center gap-0.5"><Heart className="w-3 h-3" /> {item.likes}</span>
                        <span className="flex items-center gap-0.5"><ShoppingCart className="w-3 h-3" /> {item.purchases}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-8">
                {[...Array(totalPages)].map((_, i) => (
                  <Button
                    key={i}
                    size="sm"
                    variant={page === i + 1 ? "default" : "outline"}
                    onClick={() => setPage(i + 1)}
                    className={page === i + 1 ? "bg-neon-cyan text-black" : "border-white/20"}
                  >
                    {i + 1}
                  </Button>
                ))}
              </div>
            )}
          </>
        )}

        {/* Buy Dialog */}
        <Dialog open={!!buyItem} onOpenChange={(v) => { if (!v) setBuyItem(null); }}>
          <DialogContent className="bg-zinc-900 border-white/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase">Buy This Design</DialogTitle>
            </DialogHeader>
            {buyItem && (
              <div className="space-y-4">
                <div className="flex gap-4">
                  {buyItem.design_image_base64 && (
                    <img src={`data:image/png;base64,${buyItem.design_image_base64}`} alt="" className="w-24 h-24 rounded-lg object-cover" />
                  )}
                  <div>
                    <p className="font-medium">{buyItem.title}</p>
                    <p className="text-xs text-zinc-400">by {buyItem.creator_name}</p>
                    <p className="text-xs text-purple-400 mt-1">
                      <DollarSign className="w-3 h-3 inline" /> 10% royalty goes to the designer
                    </p>
                  </div>
                </div>

                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">Put it on a...</label>
                  <Select value={buyProductType} onValueChange={setBuyProductType}>
                    <SelectTrigger className="bg-zinc-800 border-white/10" data-testid="buy-product-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10">
                      {Object.entries(PRODUCT_LABELS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">Product Name</label>
                  <Input
                    value={buyName}
                    onChange={e => setBuyName(e.target.value)}
                    className="bg-zinc-800 border-white/10"
                    data-testid="buy-product-name"
                  />
                </div>

                <Button
                  onClick={handleBuy}
                  disabled={buying}
                  className="w-full bg-neon-magenta text-black font-bold uppercase"
                  data-testid="confirm-buy-btn"
                >
                  {buying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShoppingCart className="w-4 h-4 mr-2" />}
                  Create & Buy
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
